import { CellContext, ColumnDef } from '@tanstack/react-table'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { FlexBox } from 'components/Box'
import { ProductsHeaderMenu } from 'components/Table'
import { TooltipProps } from 'components/Tooltip'
import { Alert } from 'features/alerts/types/alert'
import { AdUnitAlerts } from 'features/inventory/ad-unit/components/AdUnitAlerts'
import { AdUnitForTable } from 'features/inventory/ad-unit/components/AdUnitsTable/types/model'
import {
  AdUnitProductsAttributes,
  AdUnitTableSchema,
} from 'features/inventory/ad-unit/forms/AdUnitTableForm/schema'
import { usePriceGeniusAlwaysOn } from 'features/inventory/ad-unit/hooks/usePriceGeniusAlwaysOn'
import { AdUnitAlert } from 'features/inventory/ad-unit/types/adUnitAlert'
import { Product } from 'features/product'
import { useAbility } from 'providers/casl'
import { ProductSwitchCell } from '../../components/ProductSwitchCell'
import { useActivateAllAdUnitsProduct } from '../useActivateAllAdUnitsProduct/useActivateAllAdUnitsProduct'
import { useDeactivateAllAdUnitsProduct } from '../useDeactivateAllAdUnitsProduct/useDeactivateAllAdUnitsProduct'
import { UseProductSwitchColumns } from './types'

export const useProductSwitchColumns = ({
  tableItemsCount,
}: UseProductSwitchColumns): ColumnDef<AdUnitForTable>[] => {
  const ability = useAbility()
  const { t: tAdUnit } = useTranslation('features/adUnit')
  const { t: tProduct } = useTranslation('features/product')
  const { getValues, setValue } = useFormContext<AdUnitTableSchema>()

  const { overwritePriceGeniusState } = usePriceGeniusAlwaysOn()
  const { activateAllAdUnitsProduct } = useActivateAllAdUnitsProduct()
  const { deactivateAllAdUnitsProduct } = useDeactivateAllAdUnitsProduct()

  const productsSwitches: Product[] = [
    'prebidStack',
    'refresher',
    'viewabilityTools',
    'priceGenius',
    'unfilledRecovery',
  ]

  const column = (product: Product): ColumnDef<AdUnitForTable> => {
    const noProductAccess: boolean = ability.cannot('access', `Product-${product}`)
    const field = `${product}Enabled` as keyof AdUnitProductsAttributes

    const isNotAuthorized: boolean =
      noProductAccess || ability.cannot('update', `Product-${product}`)

    const handleToggleProductState = (enabled: boolean): void => {
      const adUnitsAttributes = getValues('adUnitsAttributes')
      if (enabled) {
        activateAllAdUnitsProduct(product)
      } else {
        deactivateAllAdUnitsProduct(product)
      }
      Object.keys(adUnitsAttributes).forEach(adUnitId => {
        setValue(`adUnitsAttributes.${adUnitId}.${field}`, enabled)
      })
    }

    const composeTooltip = (): TooltipProps | undefined => {
      if (noProductAccess) {
        return {
          content: tProduct('featureUnavailable'),
        }
      } else return undefined
    }

    const Cell = (props: CellContext<AdUnitForTable, unknown>): React.JSX.Element => {
      const { alerts } = props.row.original

      const isDisabledBasedOnOutOfPage: boolean =
        product === 'viewabilityTools' && props.row.original.outOfPage

      return (
        <FlexBox axis='x'>
          <ProductSwitchCell
            {...props}
            disabled={
              isNotAuthorized || overwritePriceGeniusState(product) || isDisabledBasedOnOutOfPage
            }
            product={product}
          />

          {alerts.length > 0 && (
            <AdUnitAlerts
              alerts={alerts.filter((alert: Alert<AdUnitAlert>): boolean =>
                alert.name.startsWith(product),
              )}
            />
          )}
        </FlexBox>
      )
    }

    return {
      accessorKey: `${product}Enabled`,
      cell: props => <Cell {...props} />,
      header: tProduct(`product.${product}`),
      meta: {
        disabledHeader: isNotAuthorized,
        headerMenu: (
          <ProductsHeaderMenu
            i18nResource={tAdUnit}
            productState={{
              disabledToggle: isNotAuthorized || tableItemsCount === 0,
              onToggle: handleToggleProductState,
            }}
          />
        ),
        nonClickable: true,
        tooltip: composeTooltip(),
      },
    }
  }

  return productsSwitches.map((product: Product): ColumnDef<AdUnitForTable> => column(product))
}
